"use client";

import { EdgenModalCreateAble } from "@/src/components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@/src/components/EdgenModal/waitForEdgenModalRoot";
import { isInterruptiveSurfaceRoute } from "@/src/hooks/onboarding/isOnboardingRoute";
import { OnboardingWelcomeModalContent } from "./OnboardingWelcomeModalContent";
import { navigateToInvestFromOnboardingWelcome } from "./navigateToInvestFromOnboardingWelcome";
import type {
  OnboardingWelcomeModalAction,
  OpenOnboardingWelcomeModalParams,
} from "./types";
import log from "logbeacon";

type WelcomeModalResult = { action: OnboardingWelcomeModalAction };

/** 防止 Strict Mode / 重复 useEffect 叠两层弹窗 */
let pendingWelcomeModal: Promise<WelcomeModalResult> | null = null;

/**
 * 打开 Onboarding Welcome 全屏弹窗（使用 EdgenModalCreateAble，PC 居中，移动端全屏）
 */
export const openOnboardingWelcomeModal = (
  params: OpenOnboardingWelcomeModalParams = {},
): Promise<WelcomeModalResult> => {
  if (pendingWelcomeModal) {
    return pendingWelcomeModal;
  }

  pendingWelcomeModal = callEdgenModalWhenRootReadyAsync(
    () =>
      new Promise<WelcomeModalResult>((resolve) => {
        let settled = false;
        const finish = (action: OnboardingWelcomeModalAction) => {
          if (settled) {
            return;
          }
          settled = true;
          pendingWelcomeModal = null;
          EdgenModalCreateAble.end(false);
          if (action === "view") {
            navigateToInvestFromOnboardingWelcome();
          } else {
            log.trace("[GTM_CLICK][catchup_dismiss]", {
              unread_count: String(params.itemCount ?? 0),
            });
          }
          resolve({ action });
        };

        EdgenModalCreateAble.call({
          children: (
            <OnboardingWelcomeModalContent
              {...params}
              onClose={(action = "close") => {
                finish(action);
              }}
            />
          ),
          className:
            "[--modal-radius:0px]  md:[--modal-radius:24px] [--modal-height:100dvh] md:[--modal-height:60vh] md:[--modal-width:400px] [--modal-width:100%]",
          contentStyle: {
            borderRadius: "var(--modal-radius)",
            width: "var(--modal-width)",
            height: "var(--modal-height)",
            overflow: "hidden",
            padding: 0,
          },
          styles: {
            body: {
              height: "100%",
            },
          },
          containerStyle: {
            height: "100%",
            width: "100%",
            maxHeight: "100%",
          },
          titleStyle: {
            minHeight: 0,
          },
          showClose: false,
          destroyOnClose: true,
        });
      }),
  ).catch((error) => {
    pendingWelcomeModal = null;
    log.trace("[GTM_VIEW][catchup_error]", {
      error_type: error instanceof Error ? error.message : "unknown",
    });
    throw error;
  });

  return pendingWelcomeModal;
};

export const openOnboardingWelcomeModalWhenReady = async (
  params: OpenOnboardingWelcomeModalParams = {},
  options?: { delayMs?: number },
): Promise<WelcomeModalResult> => {
  const delayMs = options?.delayMs ?? 3500;
  if (
    typeof window !== "undefined" &&
    isInterruptiveSurfaceRoute(window.location.pathname)
  ) {
    return { action: "close" };
  }
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
  if (
    typeof window !== "undefined" &&
    isInterruptiveSurfaceRoute(window.location.pathname)
  ) {
    return { action: "close" };
  }
  return openOnboardingWelcomeModal(params);
};
